

















import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import { PAGE_NS, PageState } from '~/store/modules/shared/page/state'
import ViewWrapper from '~/components/shared/classified/view/ViewWrapper.vue'
import CBreadcrumbs from '~/components/shared/configurable/CBreadcrumbs.vue'
import {
  computed,
  defineNuxtComponent,
  nextTick,
  onBeforeUnmount,
  useStore
} from '~/utils/nuxt3-migration'
import ConsentPrompt from '~/components/shared/consent/ConsentPrompt.vue'
import { useNamespacedStore } from '~/compositions/store'
import { getRouteMetaItem } from '~/utils/router'
import { ClassifiedViewVariant } from '~/models/classified/view/variant'
import {
  UserAgentState,
  USER_AGENT_NS
} from '~/store/modules/shared/userAgent/state'
import { useDep } from '~/compositions/dependency-container'
import DealerSiteService from '~/services/dealers/site/DealerSiteService'
import { preconnectLink } from '~/factories/head'
import { createPhotoUrlWithSize } from '~/utils/photos'
import { USER_NS } from '~/store/modules/shared/user/state'
import { SET_SHOW_CONTACT } from '~/store/modules/shared/classifieds/view/mutation-types'
import { BackendMetaItem } from '~/models/head/types'

export default defineNuxtComponent({
  name: 'ClassifiedView',
  components: {
    ViewWrapper,
    ConsentPrompt,
    CBreadcrumbs
  },
  // Using asyncData here in favor of setup useFetch due to a nuxt composition-api bug with children pages
  asyncData({ store, route }) {
    const inMapRouteMeta = getRouteMetaItem(route, 'inMap')
    return store.dispatch(`${CLASSIFIED_VIEW_NS}/createClassifiedView`, {
      slugOrId: route.params.slugOrId,
      variant: inMapRouteMeta
        ? ClassifiedViewVariant.MAP
        : ClassifiedViewVariant.DEFAULT
    })
  },
  setup() {
    const {
      state: viewState,
      commit: viewCommit,
      dispatch: viewDispatch
    } = useNamespacedStore<ClassifiedViewState>(CLASSIFIED_VIEW_NS)
    const { getters: pageGetters } = useNamespacedStore<PageState>(PAGE_NS)
    const { getters: userAgentGetters } = useNamespacedStore<UserAgentState>(
      USER_AGENT_NS
    )
    const store = useStore()

    const dealerSiteService = useDep(DealerSiteService)
    const isDealerSite = computed(() => dealerSiteService.routeIsOfDealerSite())

    const isMobile = computed(() => userAgentGetters('isMobile'))

    const breadcrumbs = computed(() => viewState.page?.breadcrumbs)
    const pageIsExplicitAndRequiresConsent = computed(() =>
      pageGetters('pageIsExplicitAndRequiresConsent')
    )

    const unsubscribe = store.subscribeAction({
      after: async action => {
        if (!process.client) {
          return
        }
        if (action.type === `${USER_NS}/loadUserSubscribable`) {
          await viewDispatch('loadClassifiedView')
          // also reset the contact form
          viewCommit(SET_SHOW_CONTACT, false)
          await nextTick()
          viewCommit(SET_SHOW_CONTACT, true)
        }
      }
    })

    onBeforeUnmount(() => {
      unsubscribe()
    })

    return {
      breadcrumbs,
      pageIsExplicitAndRequiresConsent,
      isMobile,
      isDealerSite
    }
  },
  head() {
    const { view: viewStore } = this.$store.state.classifieds

    const { page, refs, consent } = viewStore
    const { title, metadata } = page

    const nativePhotos = viewStore.classified?.compactPhotos.native.photos

    const meta = [
      ...metadata.map((metaItem: BackendMetaItem) => ({
        ...metaItem,
        hid: metaItem.name || metaItem.property
      })),
      ...[
        {
          name: 'og:url',
          content: `https://www.car.gr${this.$route.path}`
        }
      ]
    ]

    if (consent?.name === 'over18') {
      meta.push(
        { name: 'rating', content: 'adult' },
        { name: 'rating', content: 'RTA-5042-1996-1400-1577-RTA' }
      )
    }

    const link = [preconnectLink(viewStore.seoUrl, true, 'canonical')]

    if (nativePhotos?.length) {
      const [firstPhoto] = nativePhotos

      link.push({
        rel: 'preload',
        href: createPhotoUrlWithSize(firstPhoto.url, 'z'),
        as: 'image'
      })
    }

    return {
      title: title || refs?.title,
      meta,
      link
    }
  }
})
